import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ShareButtons from "../components/ShareButtons";
import FollowButton from "../components/FollowButton";
import { ArrowLeft, Play, Pause, Clock, Disc, Heart, Download } from "lucide-react";
import { useAudioPlayer, setRadioScope } from "../hooks/useAudioPlayer";
import { isLiked, toggleLike } from "../lib/userPrefs";
import { slugifyArtist } from "../lib/slug";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlaylistDetailPage() {
  const { id } = useParams();
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const { playingId: playing, handlePlay } = useAudioPlayer();

  useEffect(() => {
    const fetchPlaylist = async () => {
      try {
        const response = await axios.get(`${API}/playlists/${id}`);
        setPlaylist(response.data);
        setLiked(isLiked("cds", response.data.id));
      } catch (error) {
        console.error("Error fetching playlist:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlaylist();
  }, [id]);

  // Determina o artista principal: o nome mais frequente nas músicas
  const mainArtist = useMemo(() => {
    if (!playlist?.musics?.length) return "";
    const counts = {};
    for (const m of playlist.musics) {
      const n = (m.artist_name || "").trim();
      if (!n) continue;
      counts[n] = (counts[n] || 0) + 1;
    }
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted[0]?.[0] || "";
  }, [playlist]);

  const formatPlays = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num;
  };

  // Detect if the playlist is a single-artist album (from a CD) → scope "artist";
  // otherwise treat as multi-artist collection → scope "all" (mistura na rádio geral depois).
  const playlistArtistScope = () => {
    const musics = playlist?.musics || [];
    if (musics.length === 0) return { type: "all" };
    const uniqueArtists = new Set(musics.map((m) => (m.artist_name || "").toLowerCase()).filter(Boolean));
    if (uniqueArtists.size === 1) {
      return { type: "artist", artistName: musics[0].artist_name };
    }
    return { type: "all" };
  };

  const playAll = () => {
    if (playlist?.musics?.length > 0) {
      setRadioScope(playlistArtistScope());
      // Pass entire playlist as queue → radio mode auto-advances on track end
      handlePlay(playlist.musics[0], playlist.musics);
    }
  };

  const downloadTrack = (e, music) => {
    e.stopPropagation();
    if (!music.preview_url) {
      toast.error("Prévia indisponível para essa faixa");
      return;
    }
    const url = `${API}/musics/${music.id}/download`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`Baixando "${music.title}" (prévia 30s)...`);
  };

  const [zipping, setZipping] = useState(false);
  const downloadAll = () => {
    if (zipping) return;
    if (!playlist?.musics?.length) {
      toast.error("Playlist vazia");
      return;
    }
    setZipping(true);
    toast.info(`Preparando ZIP com ${playlist.musics.length} prévias...`);
    const url = `${API}/playlists/${id}/download`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Server takes a few seconds to assemble the zip; UI re-enables after a delay
    setTimeout(() => setZipping(false), 6000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!playlist) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <div className="max-w-4xl mx-auto px-4 pt-32 text-center">
          <h1 className="font-heading text-3xl font-bold text-white mb-4">
            Playlist não encontrada
          </h1>
          <Link to="/playlists" className="text-[#FF007F] hover:underline">
            Voltar para playlists
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      
      <main className="pt-24">
        {/* Hero */}
        <div className="relative">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(${playlist.cover_image || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1200'})`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-[#050505]/80 to-[#050505]" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <Link
              to="/playlists"
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft size={18} />
              Voltar
            </Link>
            
            <div className="flex flex-col md:flex-row items-start gap-8">
              {/* Cover */}
              <div className="w-56 h-56 rounded-2xl overflow-hidden shadow-2xl shadow-[#FF007F]/20 flex-shrink-0">
                {playlist.cover_image ? (
                  <img
                    src={playlist.cover_image}
                    alt={playlist.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#FF007F] to-[#00E5FF] flex items-center justify-center">
                    <Disc className="w-20 h-20 text-white/50" />
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1">
                <p className="text-[#FF007F] text-sm font-bold uppercase tracking-wider mb-2">
                  Playlist
                </p>
                <h1 className="font-heading text-4xl md:text-5xl font-black text-white mb-2">
                  {playlist.name}
                </h1>
                {mainArtist && (
                  <Link
                    to={`/artista/${slugifyArtist(mainArtist)}`}
                    className="inline-flex items-center gap-2 text-base sm:text-lg text-zinc-300 hover:text-[#FF007F] transition-colors mb-3 group"
                    data-testid="playlist-artist-link"
                  >
                    <span className="font-semibold">{mainArtist}</span>
                    <span className="text-xs text-zinc-500 group-hover:text-[#FF007F]/80 transition-colors">
                      Ver perfil do artista →
                    </span>
                  </Link>
                )}
                {mainArtist && (
                  <div className="mb-4">
                    <FollowButton artistName={mainArtist} slug={slugifyArtist(mainArtist)} size="md" />
                  </div>
                )}
                <p className="text-zinc-500 mb-6">
                  {playlist.music_count} músicas
                </p>
                
                {/* Actions */}
                <div className="flex items-center gap-4 flex-wrap">
                  <button
                    onClick={playAll}
                    className="flex items-center gap-2 px-8 py-3 bg-[#FF007F] hover:bg-[#FF3399] text-white font-bold rounded-full transition-all hover:scale-105"
                    data-testid="play-all-btn"
                  >
                    <Play size={20} fill="white" />
                    Tocar Tudo
                  </button>
                  <button
                    onClick={downloadAll}
                    disabled={zipping || !playlist?.musics?.length}
                    className="flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-wait text-black font-bold rounded-full transition-all shadow-lg shadow-emerald-500/25"
                    data-testid="download-all-btn"
                    title="Baixa todas as prévias (30s) em um único arquivo .zip"
                  >
                    <Download size={18} />
                    {zipping ? "Preparando..." : "Baixar Tudo"}
                  </button>
                  <button
                    onClick={() => {
                      if (!playlist) return;
                      const now = toggleLike("cds", playlist.id);
                      setLiked(now);
                      toast.success(now ? "Adicionado aos favoritos" : "Removido dos favoritos");
                    }}
                    className={`w-12 h-12 rounded-full border flex items-center justify-center transition-colors ${
                      liked
                        ? "border-[#FF007F] bg-[#FF007F]/15"
                        : "border-white/20 hover:border-[#FF007F]"
                    }`}
                    data-testid="like-playlist-btn"
                    aria-label={liked ? "Curtido" : "Curtir"}
                  >
                    <Heart
                      size={20}
                      className={liked ? "text-[#FF007F]" : "text-white"}
                      fill={liked ? "#FF007F" : "none"}
                    />
                  </button>
                  <ShareButtons url={`/playlist/${playlist.id}`} title={playlist.name} size="md" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Track List */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-[#121212] rounded-2xl border border-white/10 overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-12 gap-4 px-4 sm:px-6 py-3 border-b border-white/10 text-zinc-500 text-sm">
              <div className="col-span-1">#</div>
              <div className="col-span-7 sm:col-span-5">Título</div>
              <div className="col-span-2 hidden sm:block">Artista</div>
              <div className="col-span-2 hidden md:block text-right">Plays</div>
              <div className="col-span-1 hidden sm:block text-right">
                <Clock size={16} />
              </div>
              <div className="col-span-4 sm:col-span-1 text-right"></div>
            </div>
            
            {/* Tracks */}
            <div className="divide-y divide-white/5">
              {playlist.musics?.map((music, index) => (
                <div
                  key={music.id}
                  className={`grid grid-cols-12 gap-2 sm:gap-4 px-3 sm:px-6 py-3 sm:py-4 hover:bg-white/5 transition-colors group ${playing === music.id ? 'bg-[#FF007F]/10' : ''}`}
                  data-testid={`track-${index}`}
                >
                  {/* Number / Play */}
                  <div className="col-span-1 flex items-center">
                    {playing === music.id ? (
                      <button onClick={() => { setRadioScope(playlistArtistScope()); handlePlay(music, playlist.musics); }}>
                        <Pause size={16} fill="white" className="text-[#FF007F]" />
                      </button>
                    ) : (
                      <>
                        <span className="group-hover:hidden text-zinc-500">{index + 1}</span>
                        <button
                          onClick={() => { setRadioScope(playlistArtistScope()); handlePlay(music, playlist.musics); }}
                          className={`hidden group-hover:block ${!music.preview_url ? 'opacity-30' : ''}`}
                          disabled={!music.preview_url}
                        >
                          <Play size={16} fill="white" className="text-white" />
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* Title & Artist Image */}
                  <div className="col-span-7 sm:col-span-5 flex items-center gap-3 min-w-0">
                    {music.artist_image ? (
                      <img
                        src={music.artist_image}
                        alt={music.artist_name}
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                        <Disc className="w-5 h-5 text-zinc-600" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className={`font-medium truncate ${playing === music.id ? 'text-[#FF007F]' : 'text-white'}`}>
                        {music.title}
                      </p>
                      <p className="text-zinc-500 text-xs sm:hidden flex items-center gap-2 truncate">
                        <span className="truncate">{music.artist_name}</span>
                        <span className="opacity-50">•</span>
                        <span className="flex items-center gap-0.5 tabular-nums shrink-0 text-[#FF007F]/80">
                          <Play size={9} fill="currentColor" />
                          {formatPlays(music.plays)}
                        </span>
                      </p>
                    </div>
                  </div>
                  
                  {/* Artist (desktop only) */}
                  <div className="col-span-2 hidden sm:flex items-center text-zinc-400 truncate">
                    {music.artist_name}
                  </div>
                  
                  {/* Plays (desktop only) */}
                  <div className="col-span-2 hidden md:flex items-center justify-end text-zinc-500 tabular-nums">
                    {formatPlays(music.plays)}
                  </div>
                  
                  {/* Duration - escondida no mobile */}
                  <div className="col-span-1 hidden sm:flex items-center justify-end text-zinc-500">
                    {music.duration}
                  </div>

                  {/* Download */}
                  <div className="col-span-3 sm:col-span-1 flex items-center justify-end">
                    <button
                      onClick={(e) => downloadTrack(e, music)}
                      disabled={!music.preview_url}
                      className="p-2 rounded-full text-zinc-500 hover:text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      title="Baixar prévia (30s)"
                      data-testid={`download-track-${music.id}`}
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
